import React from 'react';
import { Avatar } from '@mui/material';

import * as ItemUtils from '../common/ItemTools';
import { buildMediaTypeNameMap } from '../common/MediaTypes';
import { EntityHoverCard, InitialsAvatar, useHoverCard } from './HoverCard';
import { SERIF_STACK } from './common/tokens';

const typeNameMap = buildMediaTypeNameMap();

// Whether a (flattened) author carries enough detail to be worth a hovercard.
// Avoids popping a card that would only repeat the name already on screen.
function authorHasPreview(author) {
  const img = ItemUtils.getAuthorImageUrl(author, author.authorImageScope);
  if (img || author.excerpt) return true;
  return Boolean(author.stats?.totalItems);
}

// Compact preview of an author, shown on hover.
function AuthorHoverCard({ author, onOpen, onFilter }) {
  const name = ItemUtils.getAuthorInfoName(author) || '?';
  const imageUrl = ItemUtils.getAuthorImageUrl(author, author.authorImageScope);
  const { excerpt, type: typeVal } = author;
  const stats = author.stats || {};
  const typeLabel = typeVal != null && typeNameMap[typeVal] ? typeNameMap[typeVal].label : null;
  const published = author.timePublished || author.timeCreated;
  const publishedLabel = published ? ItemUtils.convertUnixTimeToMoment(published) : null;
  const itemsLabel = stats.totalItems
    ? `${stats.totalItems.toLocaleString('en-US')} item${stats.totalItems === 1 ? '' : 's'}`
    : null;
  const totalTimeLabel = stats.timeNeeded
    ? ItemUtils.humanizeIntervalInMinutes(stats.timeNeeded)
    : null;
  const subheader = [typeLabel, publishedLabel, itemsLabel, totalTimeLabel]
    .filter(Boolean).join(' · ');

  return (
    <EntityHoverCard
      avatar={imageUrl
        ? <Avatar src={imageUrl} sx={{ width: 56, height: 56, flexShrink: 0 }} />
        : <InitialsAvatar name={name} size={56} fontSize={20} fontFamily={SERIF_STACK} />}
      name={name}
      subheader={subheader}
      excerpt={excerpt}
      openLabel="View author page"
      onOpen={onOpen}
      onFilter={onFilter}
    />
  );
}

export default function useAuthorHoverCard(author, {
  onCardOpen, onCardFilter, placement = 'bottom-start',
} = {}) {
  const merged = ItemUtils.mergeAuthor(author);
  const hasCard = Boolean(merged && authorHasPreview(merged));

  return useHoverCard({
    hasCard,
    placement,
    renderCard: (close) => (
      <AuthorHoverCard
        author={merged}
        onOpen={onCardOpen ? () => { close(); onCardOpen(); } : undefined}
        onFilter={onCardFilter ? () => { close(); onCardFilter(); } : undefined}
      />
    ),
  });
}
